import moment from "moment"
import React from "react"
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"
import * as Animatable from "react-native-animatable"
import Icon from "react-native-vector-icons/Ionicons"

import Base, { Order } from "../../base"
import AppButton from "../common/AppButton"
import Gap from "../common/Gap"
import EmptyOrderListItem from "./EmptyOrderListItem"

export default function OrdersListItem(props) {
  if (props.order.empty) {
    return (
      <EmptyOrderListItem
        delay={props.delay ?? 0}
        isStatusEditable={!!props.order?.isStatusEditable}
      />
    )
  }

  function getOrderDetails() {
    const tableNumber = `#${props.order.destination}`
    const timeslot = moment(props.order?.timeslot).format("HH:mm")

    // OAT Delivery
    if (props.order?.type === "OAT" && props.order?.timeslot) {
      return {
        title: "Table",
        data: tableNumber,
        footer: `at ${timeslot}`,
      }
    }

    // OAT
    if (props.order?.type === "OAT" && !props.order?.timeslot) {
      return {
        title: "Table",
        data: tableNumber,
      }
    }

    // Takeaway
    if (props.order?.type === "Takeaway" && props.order?.timeslot) {
      return {
        title: "Collect",
        data: timeslot,
      }
    }
  }

  const status = props.order.currentStatus
  const isPending = status === Order.status.PENDING
  const isCompleted = Order.completedGroup.includes(status)
  const orderDetails = getOrderDetails()

  var timeColour = isPending ? Base.colours.appDarkBlue : Base.colours.appOrange
  if (isCompleted) {
    timeColour = Base.colours.appGrey
  }

  function acceptButtonColour() {
    if (status === Order.status.ACCEPTED || status === Order.status.COMPLETED) {
      return Base.colours.appGreen
    }
    return Base.colours.appRed
  }

  function orderTouched() {
    if (props.order.isUpdating) return
    props.onPress(props.order, "details")
  }

  function buttons() {
    if (!props.isStatusEditable) {
      return null
    }

    if (isCompleted) {
      const title =
        status === "collected" && props.order.destination ? "Completed" : status
      return (
        <View style={styles.buttonsContainer}>
          <Text style={[styles.completedText, { color: acceptButtonColour() }]}>
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </Text>
        </View>
      )
    }

    if (props.order.isUpdating) {
      return (
        <View style={[styles.buttonsContainer, styles.loadingContainer]}>
          <ActivityIndicator size="small" color={Base.colours.appGrey} />
        </View>
      )
    }

    if (status === Order.status.READY) {
      return (
        <View style={[styles.buttonsContainer, styles.completedContainer]}>
          <AppButton
            style={styles.completeButton}
            title={"Completed"}
            onPress={() => props.onPress(props.order, "positive")}
          />
        </View>
      )
    }

    return (
      <View style={styles.buttonsContainer}>
        <AppButton
          style={styles.button}
          title={isPending ? "Reject" : "Cancel"}
          onPress={() => props.onPress(props.order, "negative")}
          negative={true}
        />
        <Gap size={12} horizontal />
        <AppButton
          style={[styles.button, { backgroundColor: acceptButtonColour() }]}
          title={
            isPending
              ? "Accept"
              : status === Order.status.READY
              ? "Completed"
              : "Ready"
          }
          onPress={() => props.onPress(props.order, "positive")}
        />
      </View>
    )
  }

  return (
    <Animatable.View
      style={[styles.main, Platform.OS === "web" && styles.fullWidth]}
      animation={"fadeIn"}
      delay={props.delay ?? 0}
      duration={200}
    >
      <View style={styles.cardShadow}>
        <TouchableOpacity style={styles.card} onPress={orderTouched}>
          <View style={[styles.time, { backgroundColor: timeColour }]}>
            <Text style={styles.orderDetailsTitle}>{orderDetails?.title}</Text>
            <Text style={styles.orderDetailsData}>{orderDetails?.data}</Text>
            <Text style={styles.orderDetailsFooter}>
              {orderDetails?.footer}
            </Text>
          </View>
          <View style={styles.flexContainer}>
            <View style={styles.infoContainer}>
              <View style={styles.flexContainer}>
                <Text style={styles.title}>
                  {props.order.customer.firstName}
                </Text>
                <Text style={[Base.styles.body, styles.subtitle]}>
                  {props.order.quantity}{" "}
                  {props.order.quantity === 1 ? "Item" : "Items"}
                </Text>
                <Text style={[Base.styles.body, styles.subtitle]}>
                  Submitted {moment(props.order.orderedAt).format("HH:mm")}
                </Text>
              </View>
              <Icon
                style={styles.icon}
                name="ios-arrow-forward"
                size={25}
                color={Base.colours.appRed}
              />
            </View>
            {buttons()}
          </View>
          <Text style={styles.id}>#{props.order.reference}</Text>
        </TouchableOpacity>
      </View>
    </Animatable.View>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
    marginVertical: 8,
    maxWidth: 400,
    alignSelf: "center",
    flexDirection: "row",
  },
  flexContainer: { flex: 1 },
  fullWidth: { width: "100%" },
  cardShadow: {
    flex: 1,
    backgroundColor: "white",
    borderRadius: 4,
    shadowOpacity: 0.15,
    elevation: 2,
    shadowOffset: {
      width: 1,
      height: 1,
    },
  },
  card: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "white",
    overflow: "hidden",
    borderRadius: 4,
  },
  time: {
    width: 93,
    alignItems: "center",
  },

  orderDetailsTitle: {
    color: "white",
    marginTop: 10,
    fontFamily: Base.fonts.appExtraBold,
    fontSize: 16,
  },

  orderDetailsData: {
    color: "white",
    fontFamily: Base.fonts.appExtraBold,
    fontSize: 22,
    textAlign: "center",
  },

  orderDetailsFooter: {
    color: "white",
    fontFamily: Base.fonts.appExtraBold,
    fontSize: 16,
    textAlign: "center",
  },

  infoContainer: {
    flex: 1,
    flexDirection: "row",
    padding: 12,
  },

  title: {
    flex: 1,
    fontSize: 16,
    color: Base.colours.appDarkBlue,
    fontFamily: Base.fonts.appExtraBold,
  },

  subtitle: {
    marginVertical: 2,
  },
  icon: {
    alignSelf: "center",
    marginHorizontal: 7,
  },
  buttonsContainer: {
    flexDirection: "row",
    margin: 12,
    marginTop: 0,
  },
  button: {
    flex: 1,
    height: 36,
    borderRadius: 18,
    shadowOpacity: 0,
  },

  completeButton: {
    height: 36,
    borderRadius: 18,
    shadowOpacity: 0,
    flex: 1,
    backgroundColor: Base.colours.appGrey,
  },

  completedText: {
    fontFamily: Base.fonts.appExtraBold,
    fontSize: 17,
  },

  id: {
    fontFamily: Base.fonts.appRegular,
    fontSize: 13,
    position: "absolute",
    color: Base.colours.appGrey,
    right: 4,
    top: 0,
    margin: 4,
  },

  loadingContainer: { justifyContent: "flex-end" },
  completedContainer: { justifyContent: "center" },
})
